import type { ServiceBusDeadLetterPagedResult } from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';

interface DeadLetterTableProps {
  queueName: string;
  data?: ServiceBusDeadLetterPagedResult;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  pageSize: number;
  pageSizeOptions: readonly number[];
  isBusy: boolean;
  onPageSizeChange: (value: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onSelectMessage: (sequenceNumber: number) => void;
}

export function DeadLetterTable({
  queueName,
  data,
  isLoading,
  isError,
  errorMessage,
  pageSize,
  pageSizeOptions,
  isBusy,
  onPageSizeChange,
  onPreviousPage,
  onNextPage,
  onSelectMessage,
}: DeadLetterTableProps) {
  if (isLoading) {
    return <div className="py-6 text-center text-gray-500 dark:text-gray-300">Loading dead letter messages...</div>;
  }

  if (isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300" role="alert">
        Failed to load dead letter messages. {errorMessage}
      </div>
    );
  }

  if (!data || data.totalCount === 0) {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-300">
        No messages in dead letter queue.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Showing {(data.page - 1) * data.pageSize + 1}-{Math.min(data.page * data.pageSize, data.totalCount)} of {data.totalCount}
        </p>
        <div className="flex items-center gap-2">
          <label htmlFor="service-bus-page-size" className="text-sm text-gray-700 dark:text-gray-200">
            Rows
          </label>
          <select
            id="service-bus-page-size"
            value={String(pageSize)}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm transition-colors hover:border-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <caption className="sr-only">Dead letter messages for queue {queueName}</caption>
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200">Sequence</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200">Message ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200">Reason</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200">Delivery</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200">Dead-Lettered At</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-200">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
            {data.messages.map((message) => (
              <tr key={message.sequenceNumber} className="transition-colors hover:bg-primary-50/60 dark:hover:bg-primary-900/20">
                <td className="px-4 py-3">
                  <span className="inline-flex rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                    {message.sequenceNumber}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-900 dark:text-gray-100" title={message.messageId}>
                  {truncate(message.messageId, 40)}
                </td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                  <span className="inline-flex rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    {message.deadLetterReason || 'Unknown'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{message.deliveryCount}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{formatDateTime(message.deadLetterTimeUtc)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onSelectMessage(message.sequenceNumber)}
                    className="inline-flex items-center gap-1 rounded-md bg-primary-700 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-800"
                  >
                    <EyeIcon className="h-3.5 w-3.5" />
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onPreviousPage}
          disabled={data.page <= 1 || isBusy}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          Previous
        </button>
        <span className="text-sm text-gray-600 dark:text-gray-300">
          Page {data.page} of {data.totalPages}
        </span>
        <button
          type="button"
          onClick={onNextPage}
          disabled={data.page >= data.totalPages || isBusy}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength)}...`;
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
